import { ApiClient } from '../utils/api';

/** Options accepted by RemoteConfigService constructor */
interface RemoteConfigOptions {
  apiClient: ApiClient;
  pollIntervalMs?: number; // default 60 s
}

/** Shape of the config package returned by backend */
interface RemoteConfigPayload {
  version: string;                     // monotonically increasing version/tag
  values: Record<string, unknown>;     // flat key‑value store
}

type Subscriber<T> = (value: T) => void;

/**
 * RemoteConfigService
 * -------------------
 *  • Fetches staged config blob from `/config`.
 *  • Polls every `pollIntervalMs` (default 60 s) with ETag / version optimization.
 *  • Notifies per‑key subscribers on change.
 *  • Persists last known config to localStorage for fast cold‑start.
 */
export class RemoteConfigService {
  private apiClient: ApiClient;
  private pollIntervalMs: number;
  private timer: ReturnType<typeof setInterval> | null = null;

  private version: string | null = null;
  private store: Record<string, unknown> = {};

  private subscribers: Map<string, Set<Subscriber<any>>> = new Map();

  constructor(opts: RemoteConfigOptions) {
    this.apiClient      = opts.apiClient;
    this.pollIntervalMs = opts.pollIntervalMs ?? 60_000;

    // Load cached config
    this.loadPersistedStore();
  }

  /** Initial fetch + start polling */
  async bootstrap(): Promise<void> {
    await this.fetchAndApply();
    if (!this.timer) {
      this.timer = setInterval(() => this.fetchAndApply().catch(() => void 0), this.pollIntervalMs);
    }
  }

  /** Typed getter with optional fallback */
  async get<T>(key: string, fallback?: T): Promise<T | undefined> {
    // ensure at least one fetch attempted
    if (this.version === null) await this.fetchAndApply().catch(() => void 0);
    return (this.store[key] as T) ?? fallback;
  }

  /** Get entire config store */
  async getConfig(): Promise<Record<string, unknown>> {
    // ensure at least one fetch attempted
    if (this.version === null) await this.fetchAndApply().catch(() => void 0);
    return { ...this.store };
  }

  /** Subscribe to updates for a single key; returns unsubscribe fn */
  subscribe<T>(key: string, cb: Subscriber<T>): () => void {
    if (!this.subscribers.has(key)) this.subscribers.set(key, new Set());
    const set = this.subscribers.get(key)!;
    set.add(cb as Subscriber<any>);

    // fire immediately with current value if available
    if (key in this.store) {
      cb(this.store[key] as T);
    }

    return () => { set.delete(cb as Subscriber<any>); };
  }

  /* ──────────────────────────────────
   *  Internal helpers
   * ────────────────────────────────── */

  /** Fetch config; apply if version changed */
  private async fetchAndApply(): Promise<void> {
    // attach If-None-Match style header if version known
    const headers: Record<string, string> = {};
    if (this.version) headers['If-None-Match'] = this.version;

    try {
      const res = await this.apiClient.get<RemoteConfigPayload>('/api/config', { headers });
      if (!res) return; // 304 Not Modified handled by ApiClient returning null/undefined

      if (res?.version !== this.version) {
        const oldStore = this.store;
        this.version   = res?.version ?? null;
        this.store     = res?.values ?? {};

        this.persistStore();
        this.notifyDiff(oldStore, this.store);
      }
    } catch {
      /* swallow errors; retry on next poll */
    }
  }

  /** Notify subscribers only for keys whose values changed */
  private notifyDiff(prev: Record<string, unknown>, next: Record<string, unknown>): void {
    const changedKeys = new Set<string>();

    for (const key of Object.keys(next)) {
      if (prev[key] !== next[key]) changedKeys.add(key);
    }
    for (const key of Object.keys(prev)) {
      if (!(key in next)) changedKeys.add(key); // deleted key
    }

    // Convert Set to Array before iteration
    Array.from(changedKeys).forEach(key => {
      const subs = this.subscribers.get(key);
      if (subs) {
        // Convert Set to Array before iteration
        Array.from(subs).forEach(fn => {
          fn(next[key]);
        });
      }
    });
  }

  /** Persist current store & version */
  private persistStore(): void {
    try {
      localStorage.setItem('oncade_remote_config', JSON.stringify({
        version: this.version,
        store  : this.store
      }));
    } catch { /* ignore quota */ }
  }

  /** Load persisted store */
  private loadPersistedStore(): void {
    try {
      const raw = localStorage.getItem('oncade_remote_config');
      if (raw) {
        const data = JSON.parse(raw) as { version: string; store: Record<string, unknown>; };
        this.version = data.version;
        this.store   = data.store;
      }
    } catch { /* ignore corrupt */ }
  }
}